import React, { useEffect, useRef, useState } from "react";

const Tabs = ({ tabs, visibleTabs = 3, onSelected = () => {} }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [scrollLeftStart, setScrollLeftStart] = useState(0);
  const [scrollLeftTarget, setScrollLeftTarget] = useState(0);
  const tabsRef = useRef(null);

  useEffect(() => {
    if (tabsRef.current) {
      setContainerWidth(tabsRef.current.offsetWidth);
    }
  }, [tabsRef]);

  useEffect(() => {
    const handleWindowResize = () => {
      setContainerWidth(tabsRef.current.offsetWidth);
    };

    window.addEventListener("resize", handleWindowResize);

    return () => {
      window.removeEventListener("resize", handleWindowResize);
    };
  }, []);

  useEffect(() => {
    if (isDragging) {
      return;
    }

    const smoothScroll = () => {
      const difference = scrollLeftTarget - tabsRef.current.scrollLeft;
      const easingFactor = 0.1;
      const scrollStep = difference * easingFactor;

      if (Math.abs(scrollStep) > 1) {
        tabsRef.current.scrollLeft += scrollStep;
        requestAnimationFrame(smoothScroll);
      } else {
        tabsRef.current.scrollLeft = scrollLeftTarget;
      }
    };

    smoothScroll();
  }, [isDragging, scrollLeftTarget]);

  const handleTabClick = (tab, index) => {
    onSelected(tab);
    setActiveTab(index);
  };

  const handleDragStart = (e) => {
    setIsDragging(true);
    setDragStartX(e.clientX || e.touches[0].clientX);
    setScrollLeftStart(tabsRef.current.scrollLeft);
  };

  const handleDragMove = (e) => {
    if (!isDragging) return;

    const clientX = e.clientX || e.touches[0].clientX;
    const dragDistance = clientX - dragStartX;
    const newScrollLeft = scrollLeftStart - dragDistance;

    tabsRef.current.scrollLeft = newScrollLeft;
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    setScrollLeftTarget(tabsRef.current.scrollLeft);
  };

  const handleArrowClick = (direction) => {
    const distance = direction === "left" ? -containerWidth : containerWidth;
    const newScrollLeft = tabsRef.current.scrollLeft + distance;
    tabsRef.current.scrollLeft = newScrollLeft;
    setScrollLeftTarget(newScrollLeft);
  };

  const tabWidth = containerWidth / visibleTabs;

  return (
    <div className="adb-tabs-container scroll-smooth">
      <div
        className="adb-tabs"
        ref={tabsRef}
        onMouseDown={handleDragStart}
        onMouseMove={handleDragMove}
        onMouseUp={handleDragEnd}
        onTouchStart={handleDragStart}
        onTouchMove={handleDragMove}
        onTouchEnd={handleDragEnd}
        style={{
          cursor: isDragging ? "grabbing" : "grab",
          overflowX: "hidden",
        }}
      >
        {tabs.map((tab, index) => (
          <button
            key={index}
            className={`adb-tab adb-touchable ${
              activeTab === index ? "active" : ""
            }`}
            onClick={() => handleTabClick(tab, index)}
            style={{ width: `${tabWidth}px` }}
          >
            {tab.icon && tab.icon}
            <span>{tab.label}</span>
          </button>
        ))}
        <div
          className="underline"
          style={{ width: `${tabWidth}px`, left: `${activeTab * tabWidth}px` }}
        ></div>
      </div>

      {tabs.length > visibleTabs && (
        <>
          <div
            className="adb-touchable arrow-left"
            onClick={() => handleArrowClick("left")}
          >
            {"<"}
          </div>
          <div
            className="adb-touchable arrow-right"
            onClick={() => handleArrowClick("right")}
          >
            {">"}
          </div>
        </>
      )}
    </div>
  );
};

export default Tabs;
